import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { isMobile } from 'react-device-detect';
import { useMutation, useQuery } from 'react-query';
import MainBodyLayout from '../partials/MainBodyLayout';
import Input from '../../../../components/Input';
import { encryptWithPublicKey, removeWhitespaces } from '../../../../utilities/helpers';
import { revealPassword } from '../../../Onboarding/helpers';
import { CardSettingsContext } from '../../../../contexts/card/settings/context';
import { cardSettingsScreens } from '../constants';
import { CARD_MONAVATE_PIN } from '../../queries';
import { showAlert } from '../../../../utilities/notifications';
import { useSetting } from '../../../../utilities/sharedHooks/settings';
import { PASSWORD_PUBLIC_KEY } from '../../../../utilities/sharedQueries';
import { CARD_PIN_CHANGE_KEY } from '../../mutations';
import { useUserCardProvider } from '../../../../utilities/sharedHooks/user';
import { cardProviders } from '../../constants';

const VerifyUserPassword = () => {
  const { t: translate } = useTranslation();
  const [password, setPassword] = useState('');
  const [encryptedPassword, setEncryptedPassword] = useState('');
  const history = useHistory();

  const { cardProvider } = useUserCardProvider();

  const [enablePasswordKMSEncryption] = useSetting(
    'app_features',
    'feature_toggle_enable_password_kms_encryption',
    false,
    false
  );

  const { data: publicKeyData } = useQuery(PASSWORD_PUBLIC_KEY, {
    enabled: !!enablePasswordKMSEncryption,
  });

  useEffect(() => {
    if (enablePasswordKMSEncryption && publicKeyData && password) {
      const encryptPassword = async () => {
        try {
          const preparedPassword = await encryptWithPublicKey(publicKeyData?.publicKey, password);
          setEncryptedPassword(preparedPassword);
        } catch (error) {
          // console.error(error);
        }
      };
      encryptPassword();
    }
  }, [enablePasswordKMSEncryption, publicKeyData, password]);

  const { openScreen } = useContext(CardSettingsContext);

  const { refetch: getMastercardMonavatePin, isLoading: isLoadingViewMasterMonavatePin } = useQuery(
    [
      CARD_MONAVATE_PIN,
      { password: enablePasswordKMSEncryption ? encryptedPassword : encodeURI(password) },
    ],
    {
      enabled: false,
      onSettled(data, error) {
        if (error) {
          // http status 200 response with error wrong password case
          if (!error?.request?.status) {
            showAlert({ message: error.message, type: 'danger' });
          }
        } else {
          openScreen(cardSettingsScreens.SET_PIN, { currentPin: data.pin });
        }
      },
    }
  );

  const { mutate: cardPinChangeKey, isLoading: isLoadingGalileoCardPinUrl } = useMutation(
    CARD_PIN_CHANGE_KEY,
    {
      onSettled: (data, error) => {
        if (error) {
          // http status 200 response with error wrong password case
          if (!error?.request?.status) {
            showAlert({ message: error.message, type: 'danger' });
          }
        } else {
          openScreen(cardSettingsScreens.SET_PIN, { changePinUrl: data?.url });
        }
      },
    }
  );

  const handleChange = (e) => {
    if (!e || !e.target) return;
    const { value } = e.target;
    setPassword(removeWhitespaces(value));
  };
  const handleSubmit = () => {
    if (process.env.REACT_APP_TEST_SET_PIN_SANDBOX_MONAVATE === 'true') {
      openScreen(cardSettingsScreens.SET_PIN, { currentPin: '' });
    } else if (cardProvider === cardProviders.GALILEO) {
      cardPinChangeKey({
        password: enablePasswordKMSEncryption ? encryptedPassword : encodeURI(password),
      });
    } else {
      getMastercardMonavatePin();
    }
  };

  return (
    <MainBodyLayout
      withBack
      onPrevClick={() => history.goBack()}
      title={translate('manage_card_setting_option_set_pin_title')}>
      <p className="text-f5 text-dark mt-4">{translate('verify_password_set_pin_note')}</p>
      <Input
        type="password"
        groupClassName="mt-4"
        placeholder={translate('password_input_label')}
        label={translate('password_input_label')}
        name="password"
        value={password}
        onChange={handleChange}
        required
        withFloatingLabel
        inputGroupChildren={
          <>
            <span
              className="icon icon-open-icon h4 text-shadow"
              role="button"
              onClick={revealPassword}
              onKeyDown={null}
              tabIndex={-1}
              aria-label="Reveal password"
            />
          </>
        }
      />
      <div
        className={`d-flex flex-row mt-5 pt-3 ${
          isMobile ? 'position-fixed w-95 bg-light pb-3 pe-2' : 'mx-2 px-5'
        }`}
        style={{ bottom: '0', left: '0', zIndex: 100 }}>
        <button
          disabled={!password}
          type="button"
          loading={isLoadingViewMasterMonavatePin || isLoadingGalileoCardPinUrl ? 'true' : 'false'}
          className="btn py-3 btn-primary w-100"
          onClick={handleSubmit}>
          {translate('continue_btn_title')}
        </button>
      </div>
    </MainBodyLayout>
  );
};

export default VerifyUserPassword;
